import Box from "@mui/material/Box";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";

import { Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { API_URLS } from "src/api/api-urls";
import useApiServices from "src/api/use-api-services";
import { INotificationsItemDto } from "src/types/notifications";
import { fToNowUTC } from "src/utils/formatTime";
import NotificationIcon from "./notification-icon";
import { paths } from "src/routes/paths";
import { PlatformsEnum } from "src/constants/platforms";
// ----------------------------------------------------------------------

export default function NotificationItem({
  notification,
  onClick,
}: {
  notification: INotificationsItemDto;
  onClick: () => void;
}) {
  // #region States and hooks
  const queryClient = useQueryClient();
  const router = useNavigate();
  const { usePatchApi } = useApiServices();
  // #endregion States and hooks

  // #region Services
  const { mutate: marksAsRead, isLoading: isMarking } = usePatchApi<{
    notification_id: number | string;
  }>({
    url: API_URLS.NOTIFICATIONS.read,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_URLS.NOTIFICATIONS.root] });
      queryClient.invalidateQueries({ queryKey: [API_URLS.NOTIFICATIONS.unreadCount] });
    },
    id: "",
  });
  // #endregion Services

  // #region Handlers
  const handleNotificationClick = () => {
    onClick();
    router(
      paths.custom[notification.data.platform as PlatformsEnum](
        notification.data as unknown as Record<string, string>
      )
    );
    marksAsRead({ notification_id: notification.id });
  };
  // #endregion Handlers

  const renderAvatar = (
    <ListItemAvatar>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: "background.neutral",
        }}
      >
        <NotificationIcon />
      </Stack>
    </ListItemAvatar>
  );

  const renderText = (
    <ListItemText
      disableTypography
      primary={notification?.title}
      secondary={
        <Stack sx={{ typography: "caption", color: "text.disabled" }} spacing={1}>
          <Typography>{notification.body}</Typography>
          <Typography variant="caption" fontWeight="bold">
            {fToNowUTC(new Date(notification.created_at).toUTCString())}
          </Typography>
        </Stack>
      }
    />
  );

  const renderUnReadBadge = !notification.is_read && (
    <Box
      sx={{
        top: 26,
        width: 8,
        height: 8,
        right: 20,
        borderRadius: "50%",
        bgcolor: "info.main",
        position: "absolute",
      }}
    />
  );

  return (
    <ListItemButton
      disableRipple
      sx={{
        p: 2.5,
        alignItems: "flex-start",
        borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
      }}
    >
      {renderUnReadBadge}

      {renderAvatar}

      <Stack sx={{ flexGrow: 1 }} onClick={handleNotificationClick}>
        {renderText}
      </Stack>
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function reader(data: string) {
  return (
    <Box
      dangerouslySetInnerHTML={{ __html: data }}
      sx={{
        mb: 0.5,
        "& p": { typography: "body2", m: 0 },
        "& a": { color: "inherit", textDecoration: "none" },
        "& strong": { typography: "subtitle2" },
      }}
    />
  );
}
